import { LoyaltyProgram } from '../types';
import CurrencyService from './currencyService';

export interface BestHotelValue {
  programName: string;
  hotelChain: string;
  pointsPerDollar: number;
  estimatedValue: number;
  recommendation: string;
  potentialSavings: number;
}

export interface HotelDeal {
  id: string;
  title: string;
  description: string;
  hotelChain: string;
  savings: number;
  validUntil: string;
  pointsRequired?: number;
  cashPrice?: number;
  valuePerPoint?: number;
  category: 'cash_rate' | 'points_deal' | 'status_upgrade' | 'transfer_bonus';
}

export interface HotelTransferOpportunity {
  fromProgram: string;
  toHotelProgram: string;
  transferRatio: string;
  currentPoints: number;
  transferablePoints: number;
  potentialValue: number;
  recommendedAmount: number;
  reasoning: string;
}

class HotelCalculationEngine {
  // Hotel chain points values (cents per point)
  private static readonly HOTEL_POINT_VALUES: Record<string, number> = {
    'Marriott Bonvoy': 0.8,
    'Hilton Honors': 0.5,
    'World of Hyatt': 1.5,
    'IHG One Rewards': 0.5,
    'ALL - Accor Live Limitless': 2.0,
    'Radisson Rewards': 0.4,
    'Choice Privileges': 0.6,
    'Wyndham Rewards': 1.0,
  };

  // Points earning rates (points per dollar spent)
  private static readonly EARNING_RATES: Record<string, number> = {
    'Marriott Bonvoy': 10,
    'Hilton Honors': 10,
    'World of Hyatt': 5,
    'IHG One Rewards': 10,
    'ALL - Accor Live Limitless': 2,
    'Radisson Rewards': 20,
    'Choice Privileges': 10,
    'Wyndham Rewards': 10,
  };

  /**
   * Calculate the best hotel loyalty program value for a user
   */
  static async calculateBestHotelValue(hotelPrograms: LoyaltyProgram[]): Promise<BestHotelValue | null> {
    if (hotelPrograms.length === 0) return null;

    let bestProgram: LoyaltyProgram | null = null;
    let bestValue = 0;

    for (const program of hotelPrograms) {
      const pointValue = this.HOTEL_POINT_VALUES[program.name] || 0.5;
      const currentValue = program.points * pointValue;
      
      if (currentValue > bestValue) {
        bestValue = currentValue;
        bestProgram = program;
      }
    }

    if (!bestProgram) return null;

    const pointValue = this.HOTEL_POINT_VALUES[bestProgram.name] || 0.5;
    const earningRate = this.EARNING_RATES[bestProgram.name] || 10;

    return {
      programName: bestProgram.name,
      hotelChain: this.getHotelChain(bestProgram.name),
      pointsPerDollar: earningRate,
      estimatedValue: bestProgram.points * pointValue,
      recommendation: this.generateHotelRecommendation(bestProgram),
      potentialSavings: await this.calculatePotentialSavings(bestProgram)
    };
  }

  /**
   * Generate personalized hotel deals based on user's programs
   */
  static async generatePersonalizedHotelDeals(hotelPrograms: LoyaltyProgram[]): Promise<HotelDeal[]> {
    const deals: HotelDeal[] = [];

    // Generate deals based on user's hotel programs
    for (const program of hotelPrograms) {
      const chainDeals = await this.generateChainSpecificDeals(program);
      deals.push(...chainDeals);
    }

    // Add generic hotel deals
    const genericDeals = await this.generateGenericHotelDeals();
    deals.push(...genericDeals);

    // Sort by potential savings
    return deals.sort((a, b) => b.savings - a.savings).slice(0, 6);
  }

  /**
   * Calculate hotel transfer opportunities from credit cards
   */
  static calculateHotelTransferOpportunities(
    creditCardPrograms: LoyaltyProgram[], 
    hotelPrograms: LoyaltyProgram[]
  ): HotelTransferOpportunity[] {
    const opportunities: HotelTransferOpportunity[] = [];

    // Transfer ratios from major credit card programs to hotel programs
    const transferRatios: Record<string, Record<string, string>> = {
      'Chase Ultimate Rewards': {
        'Marriott Bonvoy': '1:1',
        'World of Hyatt': '1:1',
        'IHG One Rewards': '1:1'
      },
      'American Express Membership Rewards': {
        'Marriott Bonvoy': '1:1',
        'Hilton Honors': '1:2',
        'ALL - Accor Live Limitless': '1:2'
      },
      'Citi ThankYou Points': {
        'Hilton Honors': '1:2',
        'Choice Privileges': '1:2',
        'Wyndham Rewards': '1:1'
      }
    };

    for (const creditCard of creditCardPrograms) {
      const transfers = transferRatios[creditCard.name];
      if (!transfers) continue;

      for (const hotelProgramName of Object.keys(transfers)) {
        const ratio = transfers[hotelProgramName];
        const hotelProgram = hotelPrograms.find(p => p.name === hotelProgramName);
        
        // Calculate transfer value
        const [from, to] = ratio.split(':').map(Number);
        const transferRate = to / from;
        const transferablePoints = Math.floor(creditCard.points * transferRate);
        const hotelPointValue = this.HOTEL_POINT_VALUES[hotelProgramName] || 0.5;
        const potentialValue = transferablePoints * hotelPointValue;

        if (potentialValue > 50) { // Only show worthwhile transfers
          opportunities.push({
            fromProgram: creditCard.name,
            toHotelProgram: hotelProgramName,
            transferRatio: ratio,
            currentPoints: creditCard.points,
            transferablePoints,
            potentialValue,
            recommendedAmount: Math.min(creditCard.points, 50000),
            reasoning: this.generateTransferReasoning(hotelProgramName, potentialValue)
          });
        }
      }
    }

    return opportunities.sort((a, b) => b.potentialValue - a.potentialValue);
  }

  /**
   * Generate chain-specific deals for a hotel program
   */
  private static async generateChainSpecificDeals(program: LoyaltyProgram): Promise<HotelDeal[]> {
    const deals: HotelDeal[] = [];
    const chain = this.getHotelChain(program.name);

    // Points-based deals
    if (program.points > 10000) {
      deals.push({
        id: `points_deal_${program.name}`,
        title: `${chain} Points Special`,
        description: `Redeem ${Math.min(program.points, 25000)} points for 2 nights at select properties`,
        hotelChain: chain,
        savings: Math.min(program.points, 25000) * (this.HOTEL_POINT_VALUES[program.name] || 0.5),
        validUntil: this.generateExpiryDate(),
        pointsRequired: Math.min(program.points, 25000),
        category: 'points_deal'
      });
    }

    // Status-based deals
    if (program.tier && program.tier !== 'Base') {
      deals.push({
        id: `status_deal_${program.name}`,
        title: `${program.tier} Member Exclusive`,
        description: `Extra 25% bonus points + complimentary breakfast at ${chain} hotels`,
        hotelChain: chain,
        savings: 150,
        validUntil: this.generateExpiryDate(),
        category: 'status_upgrade'
      });
    }

    return deals;
  }

  /**
   * Generate generic hotel deals
   */
  private static async generateGenericHotelDeals(): Promise<HotelDeal[]> {
    const baseDeals: Omit<HotelDeal, 'id'>[] = [
      {
        title: 'Global Hotel Sale - Up to 40% Off',
        description: 'Save up to 40% on hotels worldwide. Limited time offer for luxury properties.',
        hotelChain: 'Multiple Brands',
        savings: 300,
        validUntil: this.generateExpiryDate(),
        category: 'cash_rate'
      },
      {
        title: 'Weekend Getaway Special',
        description: 'Book 2 nights, get 1 night free at participating hotels.',
        hotelChain: 'Independent Hotels',
        savings: 200,
        validUntil: this.generateExpiryDate(),
        category: 'cash_rate'
      },
      {
        title: 'Extended Stay Bonus',
        description: 'Stay 5+ nights and earn triple points plus late checkout.',
        hotelChain: 'Major Chains',
        savings: 250,
        validUntil: this.generateExpiryDate(),
        category: 'points_deal'
      }
    ];

    return baseDeals.map((deal, index) => ({
      ...deal,
      id: `generic_${index + 1}`
    }));
  }

  /**
   * Get hotel chain from program name
   */
  private static getHotelChain(programName: string): string {
    const chainMap: Record<string, string> = {
      'Marriott Bonvoy': 'Marriott',
      'Hilton Honors': 'Hilton',
      'World of Hyatt': 'Hyatt',
      'IHG One Rewards': 'IHG',
      'ALL - Accor Live Limitless': 'Accor',
      'Radisson Rewards': 'Radisson',
      'Choice Privileges': 'Choice Hotels',
      'Wyndham Rewards': 'Wyndham'
    };
    return chainMap[programName] || programName;
  }

  /**
   * Generate hotel recommendations
   */
  private static generateHotelRecommendation(program: LoyaltyProgram): string {
    const pointValue = this.HOTEL_POINT_VALUES[program.name] || 0.5;
    const estimatedValue = program.points * pointValue;

    if (estimatedValue > 1000) {
      return `Excellent! You have enough points for multiple luxury hotel nights. Consider using points for high-value redemptions.`;
    } else if (estimatedValue > 500) {
      return `Great points balance! You can book several quality hotel nights or save for a premium property.`;
    } else if (estimatedValue > 200) {
      return `Good start! Consider earning more points through stays or credit card spending.`;
    } else {
      return `Build your points balance through hotel stays and partner activities.`;
    }
  }

  /**
   * Calculate potential savings from optimal hotel bookings
   */
  private static async calculatePotentialSavings(program: LoyaltyProgram): Promise<number> {
    try {
      const pointValue = this.HOTEL_POINT_VALUES[program.name] || 0.5;
      const estimatedValue = program.points * pointValue;
      
      // Estimate 15-25% savings from optimal redemptions
      const baseSavings = estimatedValue * 0.2;
      
      // Return savings (already in USD/cents)
      return Math.round(baseSavings);
    } catch (error) {
      console.error('Error calculating hotel savings:', error);
      return 0;
    }
  }

  /**
   * Generate transfer reasoning
   */
  private static generateTransferReasoning(hotelProgram: string, value: number): string {
    const chain = this.getHotelChain(hotelProgram);
    
    if (value > 500) {
      return `High-value transfer to ${chain}. Great for luxury hotel redemptions and status benefits.`;
    } else if (value > 200) {
      return `Good value transfer to ${chain}. Suitable for mid-tier hotel stays.`;
    } else {
      return `Consider transferring to ${chain} for specific hotel bookings.`;
    }
  }

  /**
   * Generate expiry date for deals
   */
  private static generateExpiryDate(): string {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + Math.floor(Math.random() * 30) + 15);
    return futureDate.toISOString().split('T')[0];
  }

  /**
   * Calculate hotel stay value and recommendations
   */
  static calculateHotelStayValue(
    hotelPrice: number,
    pointsRequired: number,
    programName: string
  ): { worthIt: boolean; valuePerPoint: number; recommendation: string } {
    const pointValue = this.HOTEL_POINT_VALUES[programName] || 0.5;
    const pointsValue = pointsRequired * pointValue;
    const valuePerPoint = hotelPrice / pointsRequired;

    const worthIt = valuePerPoint > pointValue;

    let recommendation = '';
    if (worthIt) {
      recommendation = `Great deal! You're getting ${(valuePerPoint * 100).toFixed(1)}¢ per point (vs. typical ${(pointValue * 100).toFixed(1)}¢).`;
    } else {
      recommendation = `Consider paying cash. You'd only get ${(valuePerPoint * 100).toFixed(1)}¢ per point value.`;
    }

    return {
      worthIt,
      valuePerPoint,
      recommendation
    };
  }
}

export default HotelCalculationEngine;